import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Divider,
  Alert,
  Collapse
} from '@mui/material';
import {
  Send,
  SmartToy,
  Close,
  ExpandMore,
  ExpandLess,
  Help,
  Settings,
  Refresh
} from '@mui/icons-material';
import axiosInstance from './axiosConfig';

const AIAssistant = ({ isOpen, onClose, context = {} }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'Hello! I\'m your HarvestAI assistant. I can help you with:\n\n• System navigation and features\n• Data analysis and insights\n• Task management and scheduling\n• Technical support\n• Best practices and tips\n\nHow can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Prepare context for AI
      const aiContext = {
        currentPage: context.currentPage || 'Unknown',
        userRole: context.userRole || 'User',
        systemFeatures: [
          'Dashboard Overview',
          'Room Management',
          'Sensor Monitoring',
          'Task Management',
          'Budget Tracking',
          'Employee Access Control',
          'UniFi Network Management',
          'AI Face Recognition',
          'Reports and Analytics'
        ],
        userQuery: inputValue
      };

      // Call AI API (you can replace this with your preferred AI service)
      const response = await axiosInstance.post('/api/ai-assistant/chat', {
        message: inputValue,
        context: aiContext,
        conversationHistory: messages.slice(-10) // Last 10 messages for context
      });

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.response || 'I apologize, but I\'m having trouble processing your request right now. Please try again later.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'I\'m sorry, I\'m experiencing technical difficulties. Please try again in a moment.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([
      {
        id: Date.now(),
        type: 'assistant',
        content: 'Hello! I\'m your HarvestAI assistant. How can I help you today?',
        timestamp: new Date()
      }
    ]);
  };

  const quickActions = [
    { label: 'How to add a room?', query: 'How do I add a new room to the system?' },
    { label: 'Check sensor status', query: 'How can I check the status of sensors?' },
    { label: 'Create a task', query: 'How do I create a new task?' },
    { label: 'View reports', query: 'Where can I find reports and analytics?' }
  ];

  const handleQuickAction = (query) => {
    setInputValue(query);
    setTimeout(() => sendMessage(), 100);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
          borderRadius: 3,
          border: '2px solid rgba(102, 126, 234, 0.3)',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SmartToy sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            AI Assistant
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={clearConversation} sx={{ color: 'white' }}>
            <Refresh />
          </IconButton>
          <IconButton onClick={() => setIsMinimized(!isMinimized)} sx={{ color: 'white' }}>
            {isMinimized ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Collapse in={!isMinimized}>
          {/* Quick Actions */}
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Quick Actions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickActions.map((action, index) => (
                <Chip
                  key={index}
                  label={action.label}
                  onClick={() => handleQuickAction(action.query)}
                  sx={{
                    background: 'rgba(102, 126, 234, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'rgba(102, 126, 234, 0.3)',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <List sx={{ p: 0 }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                    p: 0,
                    mb: 2
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '80%',
                      background: message.type === 'user' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: 2,
                      border: message.type === 'assistant' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          background: message.type === 'user' ? 'rgba(255, 255, 255, 0.2)' : '#667eea'
                        }}
                      >
                        {message.type === 'user' ? 'U' : <SmartToy sx={{ fontSize: 16 }} />}
                      </Avatar>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {message.type === 'user' ? 'You' : 'AI Assistant'}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5
                      }}
                    >
                      {message.content}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1, display: 'block' }}>
                      {message.timestamp.toLocaleTimeString()}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', p: 0, mb: 2 }}>
                  <Box sx={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 2, border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: '#667eea' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        AI is thinking...
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              )}
            </List>
            <div ref={messagesEndRef} />
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={3}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about HarvestAI..."
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />
          <IconButton
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AIAssistant; 